import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import utils from '../../utils/utils';
import { connect } from 'react-redux';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import { useOrders } from './apiHooks';
import PackerDeliveryDayMarketplaceOrderRow from './packerDeliveryDayMarketplaceOrderRow';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ORDER_PACKING_STATUS from '../../components/constants/orderPackingStatus';

const PackerDeliveryDayMarketplaceIndividual = ({ deliveryDayMarketplace }) => {
  const [hiddenTimeRanges, setHiddenTimeRanges] = useState([]);
  const [showPackedOrders, setShowPackedOrders] = useState(false);
  const [showOrdersWithoutPackingStatus, setShowOrdersWithoutPackingStatus] = useState(false);
  const [sortByOrderSize, setSortByOrderSize] = useState(false);
  // need to set packed IDs on top level to avoid order refetch nulling packed order item IDs
  const [packedItemIds, setPackedItemIds] = useState([]);

  const {
    orders,
    isLoading: ordersGetLoading,
    isError: isOrdersGetError,
    error: ordersGetError,
  } = useOrders({
    deliveryDayMarketplaceId: deliveryDayMarketplace.id,
    refetchInterval: 60000,
  });

  useEffect(() => {
    if (!orders) {
      return;
    }

    const newPackedItemIds = packedItemIds;

    const packedOrders = orders.filter((o) => o.packingStatus === ORDER_PACKING_STATUS.packed);
    packedOrders.forEach((o) => {
      o.items.forEach((oi) => {
        const existingIndex = packedItemIds.findIndex((pii) => pii.orderId === o.id && pii.itemId === oi.item.id);

        if (existingIndex === -1) {
          newPackedItemIds.push({ itemId: oi.item.id, orderId: o.id });
        }
      });
    });

    setPackedItemIds(newPackedItemIds);
  }, [orders]);

  const addPackedItem = (packedItem) => {
    const newPackedItemIds = packedItemIds.slice();
    newPackedItemIds.push(packedItem);
    setPackedItemIds(newPackedItemIds);
  };

  const removePackedItemFromIndex = (index) => {
    const newPackedItemIds = packedItemIds.slice();
    newPackedItemIds.splice(index, 1);
    setPackedItemIds(newPackedItemIds);
  };

  const filteredOrders = orders?.filter((o) => {
    let result = o.packingStatus !== ORDER_PACKING_STATUS.packed;

    if (showPackedOrders) {
      result = true;
    }

    if (!showOrdersWithoutPackingStatus) {
      result = result && !!o.packingStatus;
    }

    return result;
  });

  if (orders?.length) {
    orders.sort((a, b) => (a.timeRange.fromHour < b.timeRange.fromHour ? -1 : 1));
  }

  const orderDtosForGrouping = filteredOrders
    ? filteredOrders.map((order) => ({
        order,
        timeRange: `${utils.getFormattedTimeRange(order.timeRange)} - ${order.timeRange.serviceAreaName}`,
      }))
    : null;

  const orderDtosGroupedByFromHour = orderDtosForGrouping ? utils.groupBy(orderDtosForGrouping, 'timeRange') : null;

  const timeRangeKeys = orderDtosGroupedByFromHour
    ? Object.keys(orderDtosGroupedByFromHour).sort((a, b) => a.localeCompare(b))
    : null;

  return (
    <div>
      <Loader show={ordersGetLoading} />
      {isOrdersGetError ? <ErrorMessage error={utils.getErrorMessageFromResponse(ordersGetError)} /> : ''}

      <h3>
        <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.title'} />
      </h3>

      <div className="form-group m-b-1">
        <div className="checkbox">
          <label>
            <input type="checkbox" checked={showPackedOrders} onChange={() => setShowPackedOrders(!showPackedOrders)} />{' '}
            <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.show.packed.orders'} />
          </label>
        </div>
      </div>

      <div className="form-group m-b-1">
        <div className="checkbox">
          <label>
            <input
              type="checkbox"
              checked={showOrdersWithoutPackingStatus}
              onChange={() => setShowOrdersWithoutPackingStatus(!showOrdersWithoutPackingStatus)}
            />{' '}
            <FormattedMessage
              id={'packer.delivery.day.marketplace.inventory.individual.show.orders.without.packing.status'}
            />
          </label>
        </div>
      </div>

      <div className="form-group">
        <div className="checkbox">
          <label>
            <input type="checkbox" checked={sortByOrderSize} onChange={() => setSortByOrderSize(!sortByOrderSize)} />{' '}
            <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.sort.by.order.size'} />
          </label>
        </div>
      </div>

      {timeRangeKeys
        ? timeRangeKeys.map((timeRange, index) => {
            const timeRangeOrders = orderDtosGroupedByFromHour[timeRange];
            const hiddenIndex = hiddenTimeRanges.findIndex((htr) => htr === timeRange);

            if (sortByOrderSize) {
              timeRangeOrders.sort((a, b) => (a.order.items.length > b.order.items.length ? -1 : 1));
            } else {
              timeRangeOrders.sort((a, b) => (a.order.batchId < b.order.batchId ? -1 : 1));
            }

            return (
              <div key={index}>
                <h4
                  onClick={() => {
                    const newHiddenTimeRanges = hiddenTimeRanges.slice();
                    if (hiddenIndex >= 0) {
                      newHiddenTimeRanges.splice(hiddenIndex, 1);
                    } else {
                      newHiddenTimeRanges.push(timeRange);
                    }
                    setHiddenTimeRanges(newHiddenTimeRanges);
                  }}
                >
                  {timeRange}{' '}
                  {hiddenIndex >= 0 ? <FontAwesomeIcon icon={faCaretDown} /> : <FontAwesomeIcon icon={faCaretUp} />}
                </h4>

                {hiddenIndex >= 0 ? (
                  <div>
                    <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.hidden'} />
                  </div>
                ) : (
                  <div className="row m-b-6">
                    <div className="col-12 col-sm-12 col-md-10 m-b-6">
                      {timeRangeOrders.map((timeRangeOrder) => {
                        return (
                          <PackerDeliveryDayMarketplaceOrderRow
                            key={timeRangeOrder.order.id}
                            order={timeRangeOrder.order}
                            deliveryDayMarketplace={deliveryDayMarketplace}
                            packedItemIds={packedItemIds}
                            addPackedItem={addPackedItem}
                            removePackedItemFromIndex={removePackedItemFromIndex}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        : ''}
    </div>
  );
};

PackerDeliveryDayMarketplaceIndividual.propTypes = {
  currentUser: PropTypes.object,
  deliveryDayMarketplace: PropTypes.object.isRequired,
};

PackerDeliveryDayMarketplaceIndividual.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

export default injectIntl(connect(mapStateToProps)(PackerDeliveryDayMarketplaceIndividual));
